import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAbrirNovo } from '../hooks/useAbrirNovo';
import aviso, { SEM_SERVIDOR } from '../lib/aviso';
import {
    LuPlus, LuSearch, LuChevronLeft, LuChevronRight, LuPencil, LuEye, LuTrash2, LuBox, LuConciergeBell,
    LuExternalLink, LuImagePlus, LuStar, LuDownload,
} from 'react-icons/lu';
import { exportarListaCsv } from '../services/exportarCsv';
import PageHeader from '../components/PageHeader';
import RecordPanel, { PanelSection } from '../components/RecordPanel';
import SecaoHistorico from '../components/historico/SecaoHistorico';
import {
    ListRow, PerPage, RowAction, RowActions, RowConfirm, ListCard, ListCards, CardConfirm,
} from '../components/ListKit';
import Dialog from '../components/Dialog';
import '../components/ListKit.css';
import { useCelular } from '../hooks/useCelular';
import { useRecorte } from '../hooks/useRecorte';
// Global disfarçado — ver o comentário equivalente em ContactsPage.tsx.
import './ProductsPage.css';
import { ProductService, type Product } from '../services/ProductService';

const POR_PAGINA = [25, 50, 100];

type Modo = 'ver' | 'editar';
type Tipo = 'produto' | 'servico';
type Filtro = 'todos' | 'produto' | 'servico';

/**
 * NÃO EXISTE CAMPO DE TIPO NO MODELO. `Produto` é uma entidade só
 * (`ProductId, ProductCode, ProductBrand, ProductBalance, ProductPrice…`);
 * "serviço" é uma leitura que o front faz em cima dela.
 *
 * A regra herdada — e mantida aqui, porque mudá-la reclassificaria os
 * registros que já existem — é: **tem marca ⇒ produto; não tem ⇒ serviço.**
 *
 * O que estava quebrado e foi consertado: `marca` era opcional no formulário
 * de produto, mas é o único sinal do tipo. Cadastrar um produto sem marca
 * gravava-o como serviço, e ao reabrir o peso e o link sumiam da tela (ficavam
 * no banco, só não apareciam). Agora a marca é obrigatória quando o tipo é
 * produto, e salvar um serviço limpa os campos que são só de produto, para o
 * registro não ficar meio-a-meio.
 *
 * Isso deixa a inferência consistente daqui para a frente, mas não é o
 * conserto definitivo: um produto que realmente não tem marca continua sem
 * como ser cadastrado como produto. Para isso é preciso uma coluna de tipo
 * na tabela. Ver docs/designsystem/estado-do-redesign.md.
 */
const tipoDe = (p: Product): Tipo => (p.marca && p.marca.trim() ? 'produto' : 'servico');

interface Rascunho {
    produtoId: number | null;
    tipo: Tipo;
    nome: string;
    marca: string;
    codigo: string;
    codigoReferencia: string;
    codigoBarra: string;
    secao: string;
    preco: number;
    precoDeCusto: number;
    peso: number;
    link: string;
    descricaoDetalhada: string;
}

const VAZIO: Rascunho = {
    produtoId: null, tipo: 'produto', nome: '', marca: '', codigo: '', codigoReferencia: '',
    codigoBarra: '', secao: '', preco: 0, precoDeCusto: 0, peso: 0, link: '', descricaoDetalhada: '',
};

/**
 * Uma foto na galeria do painel, já salva ou ainda por subir.
 *
 * As duas convivem na MESMA lista porque a ordem é uma só: o usuário arruma as
 * fotos antes de salvar sem saber (nem precisar saber) quais já estão no
 * servidor. `imagemId` nulo é o que ainda não subiu; `arquivo` nulo é o que já
 * está lá. `url` é sempre object URL — o arquivo do servidor também chega por
 * download autenticado, não por link público.
 */
interface Foto {
    chave: string;
    imagemId: number | null;
    arquivo: File | null;
    url: string;
    nome: string;
}

/* O servidor recusa o que estiver fora disto (ProdutoImagemService); conferir
   aqui também evita a viagem de ida e volta para dizer não. */
const FOTO_TIPOS = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
const FOTO_MAX_BYTES = 10 * 1024 * 1024;

const paraRascunho = (p: Product): Rascunho => ({
    produtoId: p.produtoId,
    tipo: tipoDe(p),
    nome: p.nome ?? '',
    marca: p.marca ?? '',
    codigo: p.codigo ?? '',
    codigoReferencia: p.codigoReferencia ?? '',
    codigoBarra: p.codigoBarra ?? '',
    secao: p.secao ?? '',
    preco: p.preco ?? 0,
    precoDeCusto: p.precoDeCusto ?? 0,
    peso: p.peso ?? 0,
    link: p.link ?? '',
    descricaoDetalhada: p.descricaoDetalhada ?? '',
});

/* O código é obrigatório na API e o usuário nunca o informou: a tela antiga
   sorteava um número de 6 dígitos. Mantido, com o mesmo formato. */
const codigoNovo = () => String(Math.floor(Math.random() * 900000) + 100000);

const brl = (v: number) =>
    (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** Digitação de moeda: só dígitos, os dois últimos viram centavos. */
const moedaDoTexto = (texto: string) => Number(texto.replace(/\D/g, '')) / 100;

const iniciais = (nome: string) => nome.trim().split(/\s+/).slice(0, 2)
    .map(p => p[0]?.toUpperCase() ?? '').join('') || '?';

const margem = (preco: number, custo: number) =>
    preco > 0 && custo > 0 ? Math.round(((preco - custo) / preco) * 100) : null;

const ProductsPage = () => {
    const celular = useCelular();
    const [produtos, setProdutos] = useState<Product[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    /* Recorte de trabalho: abrir um produto, voltar e reencontrar a lista. */
    const [recorte, gravarRecorte] = useRecorte('pr:recorte', {
        busca: '', filtro: 'todos' as Filtro, pagina: 1, porPagina: 25,
    });
    const [busca, setBusca] = useState(recorte.busca);
    const [filtro, setFiltro] = useState<Filtro>(recorte.filtro);
    const [pagina, setPagina] = useState(recorte.pagina);
    const [porPagina, setPorPagina] = useState(recorte.porPagina);
    useEffect(() => {
        gravarRecorte({ busca, filtro, pagina, porPagina });
    }, [busca, filtro, pagina, porPagina, gravarRecorte]);

    const [aberto, setAberto] = useState<Product | null>(null);
    const [modo, setModo] = useState<Modo>('ver');
    const [rascunho, setRascunho] = useState<Rascunho>(VAZIO);
    const [salvando, setSalvando] = useState(false);
    const [erros, setErros] = useState<Record<string, string>>({});

    /* No cadastro novo, o tipo é escolhido ANTES do formulário: produto e
       serviço não pedem as mesmas coisas. `null` = ainda escolhendo. */
    const [tipoNovo, setTipoNovo] = useState<Tipo | null>(null);

    /* As duas portas da exclusão perguntam em lugares diferentes: a lixeira da
       linha pergunta NA linha, e o rodapé do painel abre o `<Dialog>` — ali não
       existe linha para pintar. Ver mensagens-do-sistema.html. */
    const [aConfirmar, setAConfirmar] = useState<number | null>(null);
    const [confirmarNoPainel, setConfirmarNoPainel] = useState(false);

    const [precoTexto, setPrecoTexto] = useState('');
    const [custoTexto, setCustoTexto] = useState('');

    /* ─── Fotos ───────────────────────────────────────────────────
       Nada de foto vai para o servidor antes de "Salvar": o que o usuário
       adiciona, remove ou promove a capa fica aqui, e o botão de salvar
       comita tudo junto. "Cancelar" não pode deixar rastro no catálogo. */
    const [fotos, setFotos] = useState<Foto[]>([]);
    const [fotosRemovidas, setFotosRemovidas] = useState<number[]>([]);
    const [carregandoFotos, setCarregandoFotos] = useState(false);
    const seletorFoto = useRef<HTMLInputElement>(null);
    /* Todo object URL criado enquanto este painel está aberto, para devolver
       a memória de uma vez quando ele fechar ou trocar de item. */
    const urlsCriadas = useRef<string[]>([]);

    /* ─── Dados ──────────────────────────────────────────────── */

    const carregar = useCallback(async () => {
        setIsLoading(true);
        try {
            const r = await ProductService.getAll();
            if (r.sucesso) setProdutos(r.dados || []);
            else aviso.erro(r.mensagem || 'Não foi possível carregar os itens.');
        } catch (e) {
            console.error(e);
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);
    // Mudar um filtro volta para a pagina 1 - mas nao na montagem, senao a
    // pagina restaurada da sessao se perde.
    const primeiraRodada = useRef(true);
    useEffect(() => {
        if (primeiraRodada.current) { primeiraRodada.current = false; return; }
        setPagina(1);
    }, [busca, filtro, porPagina]);

    /* As fotos do item aberto. Vale para os dois modos do painel: ver mostra a
       galeria, editar mexe nela. Cadastro novo (id 0) não tem o que buscar. */
    const idAberto = aberto?.produtoId ?? 0;
    useEffect(() => {
        if (!idAberto) { setFotos([]); setFotosRemovidas([]); return; }

        let vivo = true;
        setCarregandoFotos(true);
        (async () => {
            try {
                const metas = await ProductService.getImages(idAberto);
                const lista = await Promise.all(metas.map(async m => ({
                    chave: `salva-${m.imagemId}`,
                    imagemId: m.imagemId,
                    arquivo: null,
                    url: await ProductService.getImageFileUrl(m.imagemId),
                    nome: m.nomeOriginal || 'Foto',
                })));
                /* Se o painel fechou no meio do download, a limpeza já passou:
                   devolve estas aqui mesmo, senão ficam penduradas. */
                if (!vivo) { lista.forEach(f => URL.revokeObjectURL(f.url)); return; }
                lista.forEach(f => urlsCriadas.current.push(f.url));
                setFotos(lista);
                setFotosRemovidas([]);
            } catch (e) {
                console.error(e);
                if (vivo) aviso.erro('Não foi possível carregar as fotos do item.');
            } finally {
                if (vivo) setCarregandoFotos(false);
            }
        })();

        return () => { vivo = false; };
    }, [idAberto]);

    /* Fechar o painel ou trocar de item devolve a memória das imagens baixadas.
       Separado do efeito de carga porque as fotos ADICIONADAS no formulário
       também criam object URL, e elas não passam por lá. */
    useEffect(() => () => {
        urlsCriadas.current.forEach(URL.revokeObjectURL);
        urlsCriadas.current = [];
    }, [idAberto]);

    /* ─── Derivados ──────────────────────────────────────────── */

    const contagens = useMemo(() => ({
        todos: produtos.length,
        produto: produtos.filter(p => tipoDe(p) === 'produto').length,
        servico: produtos.filter(p => tipoDe(p) === 'servico').length,
    }), [produtos]);

    const filtrados = useMemo(() => {
        const t = busca.trim().toLowerCase();
        return produtos.filter(p => {
            if (filtro !== 'todos' && tipoDe(p) !== filtro) return false;
            if (!t) return true;
            return [p.nome, p.marca, p.codigo, p.codigoReferencia, p.secao]
                .some(c => (c || '').toLowerCase().includes(t));
        });
    }, [produtos, busca, filtro]);

    /* Catálogo não é dado de cliente: exporta quem enxerga a tela. Preço e custo
       vão como número, para a coluna somar na planilha. */
    const exportar = () => exportarListaCsv('produtos', filtrados, [
        { rotulo: 'Nome', valor: p => p.nome },
        { rotulo: 'Tipo', valor: p => (tipoDe(p) === 'produto' ? 'Produto' : 'Serviço') },
        { rotulo: 'Código', valor: p => p.codigo, tipo: 'codigo' },
        { rotulo: 'Código de referência', valor: p => p.codigoReferencia, tipo: 'codigo' },
        { rotulo: 'Código de barras', valor: p => p.codigoBarra, tipo: 'codigo' },
        { rotulo: 'Marca', valor: p => p.marca },
        { rotulo: 'Seção', valor: p => p.secao },
        { rotulo: 'Preço', valor: p => p.preco, tipo: 'numero' },
        { rotulo: 'Custo', valor: p => p.precoDeCusto || null, tipo: 'numero' },
        { rotulo: 'Peso', valor: p => p.peso || null, tipo: 'numero' },
        { rotulo: 'Link', valor: p => p.link },
        { rotulo: 'Descrição', valor: p => p.descricaoDetalhada },
        { rotulo: 'Cadastro', valor: p => p.dataCriacao, tipo: 'data' },
    ]);

    const totalPaginas = Math.max(1, Math.ceil(filtrados.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = filtrados.slice(inicio, inicio + porPagina);

    /* ─── Painel ─────────────────────────────────────────────── */

    const abrirVer = (p: Product) => {
        setAberto(p); setModo('ver'); setErros({}); setTipoNovo(null);
    };

    const abrirEdicao = (p: Product) => {
        setAberto(p);
        setRascunho(paraRascunho(p));
        setPrecoTexto(brl(p.preco ?? 0));
        setCustoTexto(brl(p.precoDeCusto ?? 0));
        setModo('editar'); setErros({}); setTipoNovo(tipoDe(p));
    };

    /* A paleta do Ctrl+K pediu "Novo produto": abre o formulário direto. */
    useAbrirNovo(() => abrirNovo());

    const abrirNovo = () => {
        setAberto({ produtoId: 0 } as Product);
        setRascunho({ ...VAZIO, codigo: codigoNovo() });
        setPrecoTexto(''); setCustoTexto('');
        setModo('editar'); setErros({});
        setTipoNovo(null); // escolhe o tipo primeiro
    };

    const escolherTipo = (t: Tipo) => {
        setTipoNovo(t);
        setRascunho(r => ({ ...r, tipo: t }));
    };

    const fechar = () => { setAberto(null); setErros({}); setTipoNovo(null); };

    const ehNovo = modo === 'editar' && rascunho.produtoId === null;
    const ehServico = rascunho.tipo === 'servico';

    /* ─── Fotos ──────────────────────────────────────────────── */

    const adicionarFotos = (escolhidos: FileList | null) => {
        if (!escolhidos || escolhidos.length === 0) return;

        const novas: Foto[] = [];
        for (const arquivo of Array.from(escolhidos)) {
            if (!FOTO_TIPOS.includes(arquivo.type)) {
                aviso.atencao(`"${arquivo.name}" não é uma imagem JPG, PNG, WEBP ou GIF.`);
                continue;
            }
            if (arquivo.size > FOTO_MAX_BYTES) {
                aviso.atencao(`"${arquivo.name}" passa de 10 MB.`);
                continue;
            }
            const url = URL.createObjectURL(arquivo);
            urlsCriadas.current.push(url);
            novas.push({
                chave: `nova-${Date.now()}-${novas.length}-${arquivo.name}`,
                imagemId: null,
                arquivo,
                url,
                nome: arquivo.name,
            });
        }

        if (novas.length) setFotos(f => [...f, ...novas]);
    };

    const removerFoto = (chave: string) => {
        setFotos(lista => {
            const alvo = lista.find(f => f.chave === chave);
            /* A que já está no servidor entra na fila de exclusão; a que ainda
               não subiu some sem deixar rastro. O object URL não é devolvido
               aqui: "Cancelar" ainda pode trazer tudo de volta ao reabrir. */
            if (alvo?.imagemId) setFotosRemovidas(ids => [...ids, alvo.imagemId!]);
            return lista.filter(f => f.chave !== chave);
        });
    };

    const tornarCapa = (chave: string) => {
        setFotos(lista => {
            const alvo = lista.find(f => f.chave === chave);
            if (!alvo) return lista;
            return [alvo, ...lista.filter(f => f.chave !== chave)];
        });
    };

    /**
     * Comita a galeria: apaga o que saiu, sobe o que entrou e grava a ordem
     * final. Roda depois de salvar o item, porque a foto precisa do id dele.
     */
    const sincronizarFotos = async (produtoId: number) => {
        const aRemover = [...fotosRemovidas];
        const atuais = [...fotos];

        try {
            while (aRemover.length) {
                await ProductService.deleteImage(aRemover[0]);
                aRemover.shift();
            }

            for (let i = 0; i < atuais.length; i++) {
                if (atuais[i].imagemId) continue;
                const criada = await ProductService.uploadImage(produtoId, atuais[i].arquivo!);
                atuais[i] = { ...atuais[i], imagemId: criada.imagemId, arquivo: null };
            }

            // Com uma foto só, a ordem é a que o upload já deu.
            const ids = atuais.map(f => f.imagemId!);
            if (ids.length > 1) await ProductService.reorderImages(produtoId, ids);
        } finally {
            /* Mesmo se der erro no meio: o que já subiu não pode subir de novo
               e o que já foi apagado não pode ser apagado de novo. Um segundo
               "Salvar" retoma de onde parou, em vez de duplicar. */
            setFotosRemovidas(aRemover);
            setFotos(atuais);
        }
    };

    const salvar = async () => {
        const e: Record<string, string> = {};
        if (!rascunho.nome.trim()) {
            e.nome = `Informe o nome do ${ehServico ? 'serviço' : 'produto'}.`;
        }
        /* A marca é o que distingue produto de serviço no banco (ver `tipoDe`).
           Sem ela, o produto voltaria da API como serviço. */
        if (!ehServico && !rascunho.marca.trim()) {
            e.marca = 'A marca identifica o item como produto — sem ela ele é gravado como serviço.';
        }
        if (Object.keys(e).length) { setErros(e); return; }

        setSalvando(true);
        try {
            // Serviço não tem peso, código de barras nem marca: zera para o
            // registro não ficar meio produto, meio serviço.
            const dto = {
                codigo: rascunho.codigo || codigoNovo(),
                codigoReferencia: rascunho.codigoReferencia.trim(),
                nome: rascunho.nome.trim(),
                codigoBarra: ehServico ? '' : rascunho.codigoBarra.trim(),
                marca: ehServico ? '' : rascunho.marca.trim(),
                secao: rascunho.secao.trim(),
                peso: ehServico ? 0 : rascunho.peso,
                preco: rascunho.preco,
                precoDeCusto: rascunho.precoDeCusto,
                link: rascunho.link.trim(),
                descricaoDetalhada: rascunho.descricaoDetalhada.trim(),
            };

            const r = rascunho.produtoId
                ? await ProductService.update({ produtoId: rascunho.produtoId, ...dto })
                : await ProductService.create(dto);

            if (r.sucesso) {
                const criando = !rascunho.produtoId;
                const produtoId = rascunho.produtoId || r.dados?.produtoId;

                /* Primeiro fixa o id recém-criado no rascunho: se as fotos
                   falharem e o usuário salvar de novo, o segundo clique tem de
                   ATUALIZAR este item, não cadastrar outro igual. */
                if (criando && produtoId) setRascunho(rr => ({ ...rr, produtoId }));

                const temFoto = fotos.length > 0 || fotosRemovidas.length > 0;
                if (temFoto && !produtoId) {
                    aviso.atencao('O item foi salvo, mas as fotos não foram enviadas. Abra o item e tente de novo.');
                    await carregar();
                    return;
                }

                if (temFoto) {
                    try {
                        await sincronizarFotos(produtoId!);
                    } catch (erroFoto) {
                        console.error(erroFoto);
                        /* O item está salvo — só as fotos não foram. Dizer isso
                           e manter o painel aberto, com os arquivos ainda ali
                           para um novo "Salvar". */
                        aviso.atencao(`${ehServico ? 'Serviço' : 'Produto'} salvo, mas as fotos não foram enviadas. Salve de novo para enviá-las.`);
                        await carregar();
                        return;
                    }
                }

                aviso.ok(`${ehServico ? 'Serviço' : 'Produto'} ${criando ? 'criado' : 'atualizado'}.`);
                await carregar();
                fechar();
            } else {
                aviso.erro(r.mensagem || 'Não foi possível salvar.');
            }
        } catch (err) {
            console.error(err);
            aviso.erro('Não foi possível salvar.');
        } finally {
            setSalvando(false);
        }
    };

    /* `alvo` opcional porque a exclusão tem duas portas: a lixeira da linha
       (que manda o item) e o rodapé do painel (que usa o que está em edição). */
    const excluir = async (alvo?: Product) => {
        const id = alvo?.produtoId ?? rascunho.produtoId;
        if (!id) return;
        setSalvando(true);
        try {
            const r = await ProductService.delete(id);
            if (r.sucesso) { aviso.ok('Item excluído.'); await carregar(); fechar(); }
            else aviso.erro(r.mensagem || 'Não foi possível excluir o item.');
        } catch (e) {
            console.error(e);
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setSalvando(false);
            setAConfirmar(null);
            setConfirmarNoPainel(false);
        }
    };

    /* ─── Render ─────────────────────────────────────────────── */

    const COLUNAS = '1.7fr .8fr 1fr .9fr .9fr .7fr 78px';
    const tipoAberto = aberto && aberto.produtoId ? tipoDe(aberto) : rascunho.tipo;

    /* O aviso de "nada aqui" e o rodapé de paginação servem à tabela (no
       computador) e aos cartões (no celular) — por isso saem do meio do JSX. */
    const listaVazia = (
        <div className="lk-blank">
            <div className="lk-blank-title">
                {produtos.length === 0 ? 'Nenhum item cadastrado' : 'Nada com esses filtros'}
            </div>
            <p className="lk-blank-text">
                {produtos.length === 0
                    ? 'Cadastre o que você vende: a IA responde sobre preço e descrição a partir daqui.'
                    : 'Ajuste a busca ou o filtro para ver os outros.'}
            </p>
            {produtos.length === 0 && (
                <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                    <LuPlus size={14} /> Novo cadastro
                </button>
            )}
        </div>
    );

    const rodape = (
        <div className="lk-foot">
            <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />
            <span className="lk-range">
                {inicio + 1}–{Math.min(inicio + porPagina, filtrados.length)} de {filtrados.length}
            </span>
            <span className="lk-pages">
                <button type="button" className="lk-page" aria-label="Página anterior"
                    disabled={paginaAtual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
                    <LuChevronLeft size={13} />
                </button>
                <span className="lk-page is-current">{paginaAtual}</span>
                <span className="lk-muted">de {totalPaginas}</span>
                <button type="button" className="lk-page" aria-label="Próxima página"
                    disabled={paginaAtual === totalPaginas} onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}>
                    <LuChevronRight size={13} />
                </button>
            </span>
        </div>
    );

    return (
        <div className="page-shell lk-screen pr-screen">
            <PageHeader
                title="Produtos e serviços"
                subtitle="O catálogo que a IA usa para responder sobre o que você vende."
                /* Lista vazia: a porta é a do aviso, no meio da tela. */
                actions={!isLoading && produtos.length === 0 ? undefined :
                    <div className="lk-actions">
                        <button type="button" className="lk-btn" onClick={exportar} disabled={isLoading}>
                            <LuDownload size={14} /> Exportar
                        </button>
                        <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                            <LuPlus size={14} /> Novo cadastro
                        </button>
                    </div>
                }
            />

            <div className="page-body lk-fill">
                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input type="search" placeholder="Buscar por nome, marca ou código…"
                            value={busca} onChange={e => setBusca(e.target.value)} />
                    </label>

                    {([['todos', 'Todos'], ['produto', 'Produtos'], ['servico', 'Serviços']] as [Filtro, string][])
                        .map(([id, rotulo]) => (
                            <button key={id} type="button"
                                className={`lk-pill ${filtro === id ? 'is-on' : ''}`}
                                onClick={() => setFiltro(id)}>
                                {rotulo} <b>{contagens[id]}</b>
                            </button>
                        ))}
                </div>

                {/* Celular: a grade de 7 colunas não cabe em 358px, então a linha
                    vira cartão — o que é o item, por quanto sai e de que tipo é.
                    Custo, margem e códigos ficam no painel, um toque adiante. */}
                {celular ? (
                    <>
                        <ListCards>
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(p => {
                                    const t = tipoDe(p);
                                    /* A pergunta da lixeira continua NA lista, como no
                                       computador; o que muda é a forma, porque ela não
                                       cabe numa linha de cartão. */
                                    if (aConfirmar === p.produtoId) return (
                                        <CardConfirm key={p.produtoId}
                                            question={`Excluir ${p.nome}? Não dá para desfazer.`}
                                            busy={salvando}
                                            onCancel={() => setAConfirmar(null)}
                                            onConfirm={() => excluir(p)} />
                                    );
                                    return (
                                        <ListCard
                                            key={p.produtoId}
                                            titulo={p.nome}
                                            apoio={brl(p.preco)}
                                            estado={
                                                <span className={`pr-tipo is-${t}`}>
                                                    {t === 'produto'
                                                        ? <><LuBox size={11} /> Produto</>
                                                        : <><LuConciergeBell size={11} /> Serviço</>}
                                                </span>
                                            }
                                            selected={aberto?.produtoId === p.produtoId}
                                            label={`Abrir ${p.nome}`}
                                            onOpen={() => abrirVer(p)}
                                            acoes={
                                                <RowAction icon={<LuTrash2 size={15} />} label="Excluir item" danger
                                                    onClick={() => setAConfirmar(p.produtoId)} />
                                            }
                                        />
                                    );
                                })}
                        </ListCards>
                        {!isLoading && filtrados.length === 0 && listaVazia}
                        {!isLoading && filtrados.length > 0 && rodape}
                    </>
                ) : (
                <div className="lk-table">
                    <div className="lk-scroll">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                            <div className="lk-head">Item</div>
                            <div className="lk-head">Tipo</div>
                            <div className="lk-head">Marca</div>
                            <div className="lk-head">Preço</div>
                            <div className="lk-head">Custo</div>
                            <div className="lk-head">Margem</div>
                            <div className="lk-head lk-head-acts">Ações</div>
                        </div>
                        <div className="lk-body">
                            {isLoading
                                ? Array.from({ length: 8 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(p => {
                                    const t = tipoDe(p);
                                    const m = margem(p.preco, p.precoDeCusto);
                                    if (aConfirmar === p.produtoId) return (
                                        <RowConfirm key={p.produtoId}
                                            question={`Excluir ${p.nome}? Não dá para desfazer.`}
                                            busy={salvando}
                                            onCancel={() => setAConfirmar(null)}
                                            onConfirm={() => excluir(p)} />
                                    );
                                    return (
                                        <ListRow key={p.produtoId} columns={COLUNAS}
                                            selected={aberto?.produtoId === p.produtoId}>
                                            <span className="lk-cell lk-strong"><span>{p.nome}</span></span>
                                            <span className="lk-cell">
                                                <span className={`pr-tipo is-${t}`}>
                                                    {t === 'produto'
                                                        ? <><LuBox size={11} /> Produto</>
                                                        : <><LuConciergeBell size={11} /> Serviço</>}
                                                </span>
                                            </span>
                                            <span className={`lk-cell ${p.marca ? 'lk-muted' : 'lk-empty-value'}`}>
                                                <span>{p.marca || '—'}</span>
                                            </span>
                                            <span className="lk-cell lk-num"><span>{brl(p.preco)}</span></span>
                                            <span className={`lk-cell lk-num ${p.precoDeCusto ? 'lk-muted' : 'lk-empty-value'}`}>
                                                <span>{p.precoDeCusto ? brl(p.precoDeCusto) : '—'}</span>
                                            </span>
                                            <span className={`lk-cell lk-num ${m === null ? 'lk-empty-value' : 'lk-muted'}`}>
                                                <span>{m === null ? '—' : `${m}%`}</span>
                                            </span>
                                            <RowActions>
                                                <RowAction icon={<LuEye size={14} />} label="Ver item"
                                                    onClick={() => abrirVer(p)} />
                                                <RowAction icon={<LuTrash2 size={14} />} label="Excluir item" danger
                                                    onClick={() => setAConfirmar(p.produtoId)} />
                                            </RowActions>
                                        </ListRow>
                                    );
                                })}
                        </div>
                    </div>

                    {!isLoading && filtrados.length === 0 && listaVazia}

                    {!isLoading && filtrados.length > 0 && rodape}
                </div>
                )}
            </div>

            <RecordPanel
                open={!!aberto}
                onClose={fechar}
                initials={iniciais(modo === 'editar' ? (rascunho.nome || 'Novo') : (aberto?.nome ?? ''))}
                title={modo === 'editar'
                    ? (rascunho.nome.trim() || (ehNovo
                        ? (tipoNovo ? `Novo ${tipoNovo === 'servico' ? 'serviço' : 'produto'}` : 'Novo cadastro')
                        : 'Item'))
                    : (aberto?.nome ?? '')}
                subtitle={aberto && aberto.produtoId
                    ? `${tipoAberto === 'servico' ? 'Serviço' : 'Produto'} · ${brl(aberto.preco)}`
                    : undefined}
                editing={modo === 'editar'}
                onBack={ehNovo ? undefined : () => setModo('ver')}
                actions={aberto && aberto.produtoId && (
                    <button type="button" className="rp-btn is-primary" onClick={() => abrirEdicao(aberto)}>
                        <LuPencil size={13} /> Editar
                    </button>
                )}
                footer={tipoNovo && (
                    <>
                        {!ehNovo && (
                            <button type="button" className="rp-btn is-danger" onClick={() => setConfirmarNoPainel(true)} disabled={salvando}>
                                Excluir
                            </button>
                        )}
                        <button type="button" className="rp-btn" disabled={salvando}
                            onClick={() => (ehNovo ? fechar() : setModo('ver'))}>Cancelar</button>
                        <button type="button" className="rp-btn is-primary" onClick={salvar} disabled={salvando}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </button>
                    </>
                )}
            >
                {/* ─── Modo ver ─────────────────────────────────── */}
                {modo === 'ver' && aberto && aberto.produtoId && (
                    <>
                        {/* Só aparece quando existe foto: a seção é opcional, e
                            uma faixa vazia em todo serviço sem foto seria um vão
                            por nada. */}
                        {fotos.length > 0 && (
                            <PanelSection title={fotos.length > 1 ? `Fotos (${fotos.length})` : 'Foto'}>
                                <div className="pr-fotos">
                                    {fotos.map((f, i) => (
                                        <figure key={f.chave} className="pr-foto">
                                            <img className="pr-foto-img" src={f.url} alt={f.nome} />
                                            {i === 0 && fotos.length > 1 && (
                                                <span className="pr-foto-capa">Capa</span>
                                            )}
                                        </figure>
                                    ))}
                                </div>
                            </PanelSection>
                        )}

                        <PanelSection title="Comercial">
                            <div className="rp-kv">
                                <span className="rp-k">Preço de venda</span>
                                <span className="rp-v">{brl(aberto.preco)}</span>
                                <span className="rp-k">Preço de custo</span>
                                <span className={`rp-v ${aberto.precoDeCusto ? '' : 'is-none'}`}>
                                    {aberto.precoDeCusto ? brl(aberto.precoDeCusto) : 'não informado'}
                                </span>
                                <span className="rp-k">Margem</span>
                                <span className={`rp-v ${margem(aberto.preco, aberto.precoDeCusto) === null ? 'is-none' : ''}`}>
                                    {margem(aberto.preco, aberto.precoDeCusto) === null
                                        ? 'precisa do custo'
                                        : `${margem(aberto.preco, aberto.precoDeCusto)}%`}
                                </span>
                            </div>
                        </PanelSection>

                        <PanelSection title="Identificação">
                            <div className="rp-kv">
                                <span className="rp-k">Código</span>
                                <span className="rp-v">{aberto.codigo || '—'}</span>
                                <span className="rp-k">Referência</span>
                                <span className={`rp-v ${aberto.codigoReferencia ? '' : 'is-none'}`}>
                                    {aberto.codigoReferencia || 'não informada'}
                                </span>
                                <span className="rp-k">Seção</span>
                                <span className={`rp-v ${aberto.secao ? '' : 'is-none'}`}>
                                    {aberto.secao || 'não informada'}
                                </span>
                                {tipoAberto === 'produto' && (
                                    <>
                                        <span className="rp-k">Marca</span>
                                        <span className="rp-v">{aberto.marca}</span>
                                        <span className="rp-k">Cód. de barras</span>
                                        <span className={`rp-v ${aberto.codigoBarra ? '' : 'is-none'}`}>
                                            {aberto.codigoBarra || 'não informado'}
                                        </span>
                                        <span className="rp-k">Saldo</span>
                                        <span className={`rp-v ${aberto.peso ? '' : 'is-none'}`}>
                                            {aberto.peso || 'não informado'}
                                        </span>
                                    </>
                                )}
                            </div>
                        </PanelSection>

                        <PanelSection title="Descrição">
                            {aberto.descricaoDetalhada
                                ? <p className="pr-desc">{aberto.descricaoDetalhada}</p>
                                : <p className="rp-none">Sem descrição. É ela que a IA usa para falar deste item.</p>}
                            {aberto.link && (
                                <a className="pr-link" href={aberto.link} target="_blank" rel="noreferrer">
                                    <LuExternalLink size={12} /> Abrir link do item
                                </a>
                            )}
                        </PanelSection>
                    </>
                )}

                {/* ─── Modo edição: primeiro o tipo ─────────────── */}
                {modo === 'editar' && !tipoNovo && (
                    <PanelSection title="O que você vai cadastrar?">
                        <div className="pr-escolha">
                            <button type="button" className="pr-escolha-card" onClick={() => escolherTipo('produto')}>
                                <LuBox size={18} />
                                <span className="pr-escolha-tit">Produto</span>
                                <span className="pr-escolha-txt">Item físico, com marca e código de barras</span>
                            </button>
                            <button type="button" className="pr-escolha-card" onClick={() => escolherTipo('servico')}>
                                <LuConciergeBell size={18} />
                                <span className="pr-escolha-tit">Serviço</span>
                                <span className="pr-escolha-txt">Trabalho prestado, com preço definido</span>
                            </button>
                        </div>
                    </PanelSection>
                )}

                {/* ─── Modo edição: formulário ──────────────────── */}
                {modo === 'editar' && tipoNovo && (
                    <>
                        <PanelSection title={ehServico ? 'Serviço' : 'Produto'}>
                            <div className="rp-field">
                                <label className="rp-label" htmlFor="pr-nome">
                                    {ehServico ? 'Nome do serviço' : 'Nome do produto'}
                                </label>
                                <input id="pr-nome" className="rp-input" type="text"
                                    value={rascunho.nome}
                                    placeholder={ehServico ? 'Ex.: Consultoria, manutenção…' : 'Ex.: Camiseta básica'}
                                    onChange={e => setRascunho(r => ({ ...r, nome: e.target.value }))} />
                                {erros.nome && <span className="rp-error">{erros.nome}</span>}
                            </div>

                            {!ehServico && (
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="pr-marca">Marca</label>
                                    <input id="pr-marca" className="rp-input" type="text"
                                        value={rascunho.marca}
                                        placeholder="Ex.: Nike"
                                        onChange={e => setRascunho(r => ({ ...r, marca: e.target.value }))} />
                                    {erros.marca
                                        ? <span className="rp-error">{erros.marca}</span>
                                        : <span className="pr-hint">
                                            É a marca que identifica o item como produto no cadastro.
                                          </span>}
                                </div>
                            )}
                        </PanelSection>

                        <PanelSection title="Preços">
                            <div className="rp-two">
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="pr-preco">Preço de venda</label>
                                    <input id="pr-preco" className="rp-input" type="text" inputMode="numeric"
                                        value={precoTexto} placeholder="R$ 0,00"
                                        onChange={e => {
                                            const v = moedaDoTexto(e.target.value);
                                            setPrecoTexto(brl(v));
                                            setRascunho(r => ({ ...r, preco: v }));
                                        }} />
                                </div>
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="pr-custo">Preço de custo</label>
                                    <input id="pr-custo" className="rp-input" type="text" inputMode="numeric"
                                        value={custoTexto} placeholder="R$ 0,00"
                                        onChange={e => {
                                            const v = moedaDoTexto(e.target.value);
                                            setCustoTexto(brl(v));
                                            setRascunho(r => ({ ...r, precoDeCusto: v }));
                                        }} />
                                </div>
                            </div>
                            {margem(rascunho.preco, rascunho.precoDeCusto) !== null && (
                                <p className="pr-hint">
                                    Margem de {margem(rascunho.preco, rascunho.precoDeCusto)}%.
                                </p>
                            )}
                        </PanelSection>

                        <PanelSection title="Identificação">
                            <div className="rp-two">
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="pr-codigo">Código</label>
                                    <input id="pr-codigo" className="rp-input" type="text"
                                        value={rascunho.codigo}
                                        onChange={e => setRascunho(r => ({ ...r, codigo: e.target.value }))} />
                                </div>
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="pr-ref">Referência</label>
                                    <input id="pr-ref" className="rp-input" type="text"
                                        value={rascunho.codigoReferencia}
                                        onChange={e => setRascunho(r => ({ ...r, codigoReferencia: e.target.value }))} />
                                </div>
                            </div>

                            <div className="rp-field">
                                <label className="rp-label" htmlFor="pr-secao">Seção</label>
                                <input id="pr-secao" className="rp-input" type="text"
                                    value={rascunho.secao} placeholder="Ex.: Vestuário"
                                    onChange={e => setRascunho(r => ({ ...r, secao: e.target.value }))} />
                            </div>

                            {!ehServico && (
                                <div className="rp-two">
                                    <div className="rp-field">
                                        <label className="rp-label" htmlFor="pr-barra">Código de barras</label>
                                        <input id="pr-barra" className="rp-input" type="text"
                                            value={rascunho.codigoBarra}
                                            onChange={e => setRascunho(r => ({ ...r, codigoBarra: e.target.value }))} />
                                    </div>
                                    <div className="rp-field">
                                        {/* O campo se chamava "Peso (kg)" e aceitava decimais, mas
                                            grava em ProductBalance e o DTO é int: a casa decimal
                                            era descartada em silêncio. Rotulado pelo que ele é. */}
                                        <label className="rp-label" htmlFor="pr-saldo">Saldo</label>
                                        <input id="pr-saldo" className="rp-input" type="number" min={0} step={1}
                                            value={rascunho.peso}
                                            onChange={e => setRascunho(r => ({ ...r, peso: Math.trunc(Number(e.target.value) || 0) }))} />
                                    </div>
                                </div>
                            )}
                        </PanelSection>

                        <PanelSection title="Descrição">
                            <div className="rp-field">
                                <label className="rp-label" htmlFor="pr-desc">Descrição detalhada</label>
                                <textarea id="pr-desc" className="rp-input pr-textarea" rows={5}
                                    value={rascunho.descricaoDetalhada}
                                    placeholder={ehServico
                                        ? 'O que está incluso, como funciona, prazo…'
                                        : 'Material, tamanhos, cores, o que acompanha…'}
                                    onChange={e => setRascunho(r => ({ ...r, descricaoDetalhada: e.target.value }))} />
                                <span className="pr-hint">É deste texto que a IA tira o que falar do item.</span>
                            </div>

                            <div className="rp-field">
                                <label className="rp-label" htmlFor="pr-link">Link</label>
                                <input id="pr-link" className="rp-input" type="url"
                                    value={rascunho.link} placeholder="https://…"
                                    onChange={e => setRascunho(r => ({ ...r, link: e.target.value }))} />
                            </div>
                        </PanelSection>

                        {/* Fotos: opcional, e nada sobe antes de "Salvar". */}
                        <PanelSection title="Fotos">
                            <input ref={seletorFoto} type="file" hidden multiple
                                accept="image/jpeg,image/png,image/webp,image/gif"
                                onChange={e => { adicionarFotos(e.target.files); e.target.value = ''; }} />

                            {fotos.length > 0 && (
                                <div className="pr-fotos">
                                    {fotos.map((f, i) => (
                                        <figure key={f.chave} className="pr-foto">
                                            <img className="pr-foto-img" src={f.url} alt={f.nome} />
                                            {i === 0 && fotos.length > 1 && (
                                                <span className="pr-foto-capa">Capa</span>
                                            )}
                                            {/* Sempre visíveis: sem hover, regra do produto. */}
                                            <div className="pr-foto-acoes">
                                                {i > 0 && (
                                                    <button type="button" className="pr-foto-btn"
                                                        aria-label={`Usar ${f.nome} como capa`}
                                                        title="Usar como capa"
                                                        onClick={() => tornarCapa(f.chave)}>
                                                        <LuStar size={12} />
                                                    </button>
                                                )}
                                                <button type="button" className="pr-foto-btn is-danger"
                                                    aria-label={`Remover ${f.nome}`}
                                                    title="Remover foto"
                                                    onClick={() => removerFoto(f.chave)}>
                                                    <LuTrash2 size={12} />
                                                </button>
                                            </div>
                                        </figure>
                                    ))}
                                </div>
                            )}

                            <button type="button" className="pr-foto-add" disabled={carregandoFotos}
                                onClick={() => seletorFoto.current?.click()}>
                                <LuImagePlus size={14} />
                                {carregandoFotos
                                    ? 'Carregando as fotos…'
                                    : fotos.length ? 'Adicionar mais fotos' : 'Adicionar fotos'}
                            </button>

                            <span className="pr-hint">
                                Opcional. JPG, PNG, WEBP ou GIF, até 10 MB cada.
                                {fotos.length > 1 && ' A primeira é a capa — a estrela troca qual é.'}
                            </span>
                        </PanelSection>
                    </>
                )}

                {/* Histórico: seção no fim do modo ver (painel sem abas). */}
                {modo === 'ver' && aberto?.produtoId && (
                    <SecaoHistorico entidade="produto" id={aberto.produtoId} />
                )}
            </RecordPanel>

            {/* A exclusão pedida de DENTRO do painel não tem linha para pintar,
                então usa o diálogo. Ver mensagens-do-sistema.html. */}
            <Dialog
                open={confirmarNoPainel}
                onClose={() => setConfirmarNoPainel(false)}
                title={`Excluir ${rascunho.nome.trim() || 'este item'}?`}
                confirmLabel="Excluir"
                danger
                busy={salvando}
                onConfirm={() => excluir()}
            >
                O item sai do catálogo que a IA usa para responder sobre o que você vende.
                Não dá para desfazer.
            </Dialog>
        </div>
    );
};

export default ProductsPage;
